import Modal from "@/components/ui/Modal";
import UploadOutfitBackground from "@/components/ui/UploadOutfitBackground";
import { CustomButton } from "@/components/ui/Buttons";
import { resetUserChanges, updateUser } from "@/redux/feature/user/userSlice";
import { useUpdateCreatorMutation } from "@/redux/feature/user/userApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { FileDetails, TUser } from "@/types/types";
import { handleApiRequest } from "@/utils/handleApiRequest";
import { ImagePlus, Pencil, Shirt, X } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

type PortfolioKind = "wardrobe" | "background";

const PortfolioGallery = ({
  kind,
  files,
}: {
  kind: PortfolioKind;
  files: FileDetails[];
}) => {
  const isWardrobe = kind === "wardrobe";
  const title = isWardrobe ? "Wardrobe" : "Backgrounds";
  const description = isWardrobe
    ? "Show brands the outfits you can wear on camera."
    : "Show the spaces where you can film content.";

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#FFF4EC] text-[#F56600]">
          {isWardrobe ? <Shirt size={19} /> : <ImagePlus size={19} />}
        </div>
        <div>
          <h6 className="text-sm font-medium text-[#101828]">{title}</h6>
          <p className="mt-1 text-xs leading-5 text-[#667085]">{description}</p>
        </div>
      </div>

      {files.length ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="aspect-square overflow-hidden rounded-lg border border-[#EAECF0] bg-[#F2F4F7]"
            >
              <img
                src={(file.live_url || file.local_url) as string}
                alt={file.name || `${title} option ${index + 1}`}
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-[#D0D5DD] bg-[#FCFCFD] px-4 py-8 text-center">
          <ImagePlus className="mb-2 text-[#98A2B3]" size={22} />
          <span className="text-xs font-medium text-[#344054]">
            No {isWardrobe ? "outfits" : "backgrounds"} added yet
          </span>
        </div>
      )}
    </div>
  );
};

const WardrobeBackgroundPortfolio = ({ data }: { data: TUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const userSliceData = useAppSelector((state) => state.user.user);
  const [updateCreator, { isLoading }] = useUpdateCreatorMutation();
  const imageFiles = [
    ...(userSliceData?.wardrobeStyles ?? []),
    ...(userSliceData?.availableBackgrounds ?? []),
  ];
  const hasIncompleteUploads = imageFiles.some((file) => !file.live_url);

  const wardrobeStyles = (data?.wardrobeStyles ?? []) as FileDetails[];
  const availableBackgrounds = (data?.availableBackgrounds ??
    []) as FileDetails[];

  const handleCancel = () => {
    dispatch(resetUserChanges(data));
    setIsOpen(false);
  };

  const handleSave = async () => {
    if (isLoading) return;
    if (hasIncompleteUploads) {
      toast.error("Wait for uploads to finish, or retry/remove failed images.");
      return;
    }
    if (!data?.user_id) {
      toast.error("User information is missing");
      return;
    }

    await handleApiRequest({
      payload: {
        id: data.user_id,
        body: {
          wardrobeStyles: userSliceData?.wardrobeStyles ?? [],
          availableBackgrounds: userSliceData?.availableBackgrounds ?? [],
        },
      },
      requestFunction: updateCreator,
      onSuccess: () => {
        toast.success("Wardrobe and backgrounds updated!");
        setIsOpen(false);
      },
      onError: () => toast.error("Failed to update. Please try again."),
    });
  };

  return (
    <>
      <section className="w-full sm:w-[69%] mx-auto lg:mx-0">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div className="space-y-2.5">
            <h5 className="text-lg font-semibold leading-5 tracking-[-1%]">
              Wardrobe & Backgrounds
            </h5>
            <p className="max-w-[44ch] leading-5 text-[#475467]">
              Help brands picture their content by showcasing your on-camera
              outfits and filming spaces.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-xs font-medium text-[#344054] transition-colors hover:bg-[#F9FAFB]"
          >
            <Pencil size={14} />
            Edit
          </button>
        </div>

        <div className="flex flex-col gap-8 rounded-xl border border-[#E4E7EC] bg-white p-6 md:flex-row md:gap-6">
          <PortfolioGallery kind="wardrobe" files={wardrobeStyles} />
          <div className="hidden w-px shrink-0 bg-[#EAECF0] md:block" />
          <div className="h-px shrink-0 bg-[#EAECF0] md:hidden" />
          <PortfolioGallery kind="background" files={availableBackgrounds} />
        </div>
      </section>

      <Modal
        preventCloseOnClickOutside
        width="700px"
        isOpen={isOpen}
        setOpen={setIsOpen}
        padding="0px"
      >
        <section className="flex max-h-[90vh] flex-col rounded-2xl bg-white">
          <div className="flex shrink-0 items-center justify-between border-b border-[#F2F4F7] px-5 py-4">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-[#FF6600] px-2 py-1 text-[11px] text-white">
                Edit
              </span>
              <h2 className="text-xl font-medium tracking-[-0.7px] text-[#060B13]">
                Wardrobe & Backgrounds
              </h2>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-[#98A2B3]"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 space-y-8 overflow-y-auto px-5 py-5">
            <div>
              <h3 className="text-sm font-medium text-[#121217]">
                Wardrobe options
              </h3>
              <p className="mt-1 text-xs text-[#667085]">
                Upload up to 4 outfits and tag each style so brands can find the
                right look quickly.
              </p>
              <UploadOutfitBackground
                mode="outfit"
                useExternalState
                externalFiles={
                  (userSliceData?.wardrobeStyles ?? []) as FileDetails[]
                }
                onExternalFilesChange={(files) =>
                  dispatch(
                    updateUser({ field: "wardrobeStyles", value: files }),
                  )
                }
              />
            </div>

            <div className="border-t border-[#F2F4F7] pt-6">
              <h3 className="text-sm font-medium text-[#121217]">
                Background options
              </h3>
              <p className="mt-1 text-xs text-[#667085]">
                Upload up to 4 filming spaces and label the setting for better
                visibility.
              </p>
              <UploadOutfitBackground
                mode="background"
                useExternalState
                externalFiles={
                  (userSliceData?.availableBackgrounds ?? []) as FileDetails[]
                }
                onExternalFilesChange={(files) =>
                  dispatch(
                    updateUser({
                      field: "availableBackgrounds",
                      value: files,
                    }),
                  )
                }
              />
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#F2F4F7] px-5 py-4">
            <CustomButton
              variant="outline"
              border="1px solid #E4E4E4"
              fontSize="14px"
              fontWeight="600"
              color="#4D4D4D"
              padding="5px 10.5px"
              borderRadius="200px"
              onClick={handleCancel}
            >
              Cancel
            </CustomButton>
            <CustomButton
              backgroundColor="#FF6600"
              fontSize="14px"
              fontWeight="600"
              color="white"
              padding="5px 10.5px"
              borderRadius="200px"
              onClick={handleSave}
              disabled={isLoading || hasIncompleteUploads}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </CustomButton>
          </div>
        </section>
      </Modal>
    </>
  );
};

export default WardrobeBackgroundPortfolio;
